import React from 'react';
import {
  Grid,
  TextField,
  IconButton,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import PropTypes from 'prop-types';

const ConfiguracaoPrestacoes = ({ formData, onUpdateForm, errors }) => {
  const handleAddPrestacao = () => {
    const novaPrestacao = {
      mes: `Prestação ${formData.prestacoes.length + 1}`,
      valor: '', // Valor vazio para entrada manual
      dataVencimento: new Date(),
      referenciasPagamentoJSON: {},
      tipo: 'BASE',
      vinculoId: null
    };

    const novasPrestacoes = [...formData.prestacoes, novaPrestacao];
    onUpdateForm({ prestacoes: novasPrestacoes });
  };

  const handleRemovePrestacao = (index) => {
    const novasPrestacoes = formData.prestacoes.filter((_, i) => i !== index);
    onUpdateForm({ prestacoes: novasPrestacoes });
  };

  const handlePrestacaoChange = (index, campo, valor) => {
    const novasPrestacoes = [...formData.prestacoes];
    novasPrestacoes[index] = {
      ...novasPrestacoes[index],
      [campo]: valor
    };

    onUpdateForm({ prestacoes: novasPrestacoes });
  };

  // Calcular total atual das prestações
  const totalAtual = formData.prestacoes.reduce((sum, p) => sum + Number(p.valor || 0), 0);
  const diferenca = Math.abs(totalAtual - formData.valorTotal);
  const temDiferenca = diferenca > 0.01;
  const excedeuTotal = totalAtual > Number(formData.valorTotal);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
      <Box>
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Configure as prestações base do plano. Estas servirão de modelo quando 
            estudantes forem vinculados ao plano.
          </Alert>
        </Box>

        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Prestações</Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={handleAddPrestacao}
          >
            Adicionar Prestação
          </Button>
        </Box>

        {errors?.prestacoes && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.prestacoes}
          </Alert>
        )}

        {excedeuTotal ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            O valor total das prestações ({totalAtual.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'MZN'
            })}) excede o valor total do plano ({Number(formData.valorTotal).toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'MZN'
            })})
          </Alert>
        ) : temDiferenca && (
          <Alert severity="info" sx={{ mb: 2 }}>
            O valor total das prestações ({totalAtual.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'MZN'
            })}) é diferente do valor total do plano ({Number(formData.valorTotal).toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'MZN'
            })})
          </Alert>
        )}

        <Grid container spacing={2}>
          {formData.prestacoes.map((prestacao, index) => (
            <Grid item xs={12} key={index}>
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Descrição"
                        value={prestacao.mes}
                        onChange={(e) => handlePrestacaoChange(index, 'mes', e.target.value)}
                        helperText="Ex: Janeiro 2024"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Valor Base"
                        type="number"
                        value={prestacao.valor}
                        onChange={(e) => handlePrestacaoChange(index, 'valor', e.target.value)}
                        helperText="Valor base para esta prestação"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Data de Vencimento"
                        value={prestacao.dataVencimento}
                        onChange={(date) => handlePrestacaoChange(index, 'dataVencimento', date)}
                        format="dd/MM/yyyy"
                        slotProps={{ 
                          textField: { 
                            fullWidth: true,
                            helperText: "Data limite para pagamento"
                          } 
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <IconButton
                        color="error"
                        onClick={() => handleRemovePrestacao(index)}
                        title="Remover prestação"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {formData.prestacoes.length === 0 && (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography color="text.secondary">
              Nenhuma prestação configurada. Clique em &quot;Adicionar Prestação&quot; para começar.
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Total de prestações: {formData.prestacoes.length}<br />
            Valor total configurado: {totalAtual.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'MZN'
            })}
          </Typography>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

ConfiguracaoPrestacoes.propTypes = {
  formData: PropTypes.shape({
    prestacoes: PropTypes.arrayOf(
      PropTypes.shape({
        mes: PropTypes.string,
        valor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        dataVencimento: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        referenciasPagamentoJSON: PropTypes.object,
        vinculoId: PropTypes.number
      })
    ),
    valorTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired,
  onUpdateForm: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    prestacoes: PropTypes.string
  })
};

export default ConfiguracaoPrestacoes;